import type { FeaturesSectionContent, ScrollRevealConfig } from "@site-factory/config";
import { cn } from "../utils";
import { SectionTitle, SectionSubtitle } from "../typography/Typography";
import { Card, CardContent } from "../components/Card";
import { BandedSection, type BandedSectionVariant } from "../components/BandedSection";
import { AnimateOnScroll, AnimatedChild } from "../animations";
import { AnimatedIcon } from "../components/AnimatedIcon";
import { Icon, iconNames, type IconName } from "../icons";

export interface FeaturesSectionProps extends FeaturesSectionContent {
  className?: string;
  bandVariant?: BandedSectionVariant;
  /** Optional scroll reveal animation configuration */
  scrollReveal?: ScrollRevealConfig;
}

export function FeaturesSection({
  title,
  subtitle,
  features,
  columns = 3,
  variant = "grid",
  className,
  bandVariant = "plain",
  scrollReveal = { enabled: true, variant: "fadeUp", staggerChildren: 0.1 },
}: FeaturesSectionProps) {
  const gridCols = {
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-2 lg:grid-cols-4",
  };

  return (
    <BandedSection variant={bandVariant} className="py-16 md:py-24" contentClassName={className}>
      {(title || subtitle) && (
        <AnimateOnScroll variant="fadeDown" duration={0.5}>
          <div className="text-center mb-12">
            {title && <SectionTitle>{title}</SectionTitle>}
            {subtitle && <SectionSubtitle className="mt-4">{subtitle}</SectionSubtitle>}
          </div>
        </AnimateOnScroll>
      )}

      <AnimateOnScroll
        className={cn(
          variant === "grid" ? `grid gap-8 ${gridCols[columns]}` : "space-y-6"
        )}
        {...scrollReveal}
      >
        {features.map((feature, index) => {
          const isIconName = feature.icon && iconNames.includes(feature.icon as IconName);
          return (
            <AnimatedChild key={index}>
              <Card className="p-6 bg-[var(--color-surface,#fff)] text-[var(--color-text,#0f172a)] shadow-sm-2 hover:shadow-md-2 transition h-full border border-neutral-200/60">
                <CardContent className="p-0 space-y-3 group">
                  <div className="flex items-center gap-3">
                    {feature.icon && (
                      <AnimatedIcon variant="scale" className="h-12 w-12 rounded-xl bg-primary/10 text-primary shrink-0">
                        {isIconName ? (
                          <Icon name={feature.icon as IconName} className="w-6 h-6" />
                        ) : (
                          <span className="text-2xl">{feature.icon}</span>
                        )}
                      </AnimatedIcon>
                    )}
                    <h3 className="text-lg font-semibold">{feature.title}</h3>
                  </div>
                  <p
                    className="text-sm"
                    style={{ color: "var(--color-text, #0f172a)", opacity: 0.82 }}
                  >
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            </AnimatedChild>
          );
        })}
      </AnimateOnScroll>
    </BandedSection>
  );
}
